import {grey} from '@mui/material/colors'
import CssBaseline from '@mui/material/CssBaseline'
import DefaultPropsProvider from '@mui/material/DefaultPropsProvider'
import GlobalStyles from '@mui/material/GlobalStyles'
import {ThemeProvider} from '@mui/material/styles'
import type {ComponentsPropsList} from '@mui/material/styles'
import {memo, type PropsWithChildren} from 'react'
import {Link as RouterLink, type LinkProps as RouterLinkProps} from 'react-router'

import {appTheme} from '../../theme/appTheme'

const LinkBehavior = memo(function LinkBehavior(props: Omit<RouterLinkProps, 'to'> & {
  href: RouterLinkProps['to']
}) {
  const {href, ...other} = props
  return <RouterLink to={href} {...other} />
})

type defaultPropsType = {
  [P in keyof ComponentsPropsList]?: Partial<ComponentsPropsList[P]>;
}
const defaultProps: defaultPropsType = {
  MuiTooltip: {
    enterDelay: 300,
    enterNextDelay: 100,
  },
  MuiFormControl: {
    margin: 'dense',
    fullWidth: true,
  },
  MuiTextField: {
    margin: 'dense',
    fullWidth: true,
  },
  MuiLink: {
    component: LinkBehavior,
  },
  MuiButtonBase: {
    LinkComponent: LinkBehavior,
  },
  MuiListItem: {
    component: 'div',
  },
  MuiList: {
    component: 'div',
  },
}

// `defaultProps` stays out of theme/appTheme: `MuiLink.component` is the router's link, and a theme importing
// from `boot/` closes an import cycle the build rejects.
export const AppTheme = memo(function AppTheme({children}: PropsWithChildren) {
  return <ThemeProvider theme={appTheme}>
    <CssBaseline/>
    <GlobalStyles
      styles={{
        body: {
          backgroundColor: grey['100'],
        },
        '*': {
          scrollbarWidth: 'thin',
        },
        '.ellipsis-container': {
          minWidth: '1px', // cannot use overflow: hidden without breaking button that use negative margins
          maxWidth: '100%',
        },
        '.nowrap': {
          whiteSpace: 'nowrap',
        },
        '.ellipsis': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '.flex1': {
          flex: 1,
        },
        '.stack-row': {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: appTheme.spacing(1),
        },
        '.stack-column': {
          display: 'flex',
          flexDirection: 'column',
          gap: appTheme.spacing(1),
        },
        '.stack-row-space': {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: appTheme.spacing(1),
          justifyContent: 'space-between',
          flex: 1,
        },
        '@keyframes optimistic-pulse': {
          '0%': {opacity: 1},
          '50%': {opacity: 0.4},
          '100%': {opacity: 1},
        },
        '.optimistic': {
          animation: 'optimistic-pulse 2s ease-in-out 0.5s infinite',
        },
        '.optimistic-error': {
          color: appTheme.palette.error.main,
          '*': {
            color: appTheme.palette.error.main,
          },
        },
        'input[type=number]': {
          MozAppearance: 'textfield',
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        },
      }}
    />
    <DefaultPropsProvider value={defaultProps}>
      {children}
    </DefaultPropsProvider>
  </ThemeProvider>
})
